import {gql} from "@apollo/client";
import {CORE_TAG_FIELDS} from "./tagQueries";

const CORE_PLAYBOOK_FIELDS = gql`
  fragment CorePlaybookFields on Playbook {
    id
    name
    identifier
    description
    isDeleted
    isApproved
    isArchived
    isDisabled
    isDynamic
    createdAt
    updatedAt
    playbookVersion
    dataSource
  }
`;

const PLAYBOOK_DATASET = gql`
  fragment PlaybookDataset on PlaybookDataset {
    id
    datasetId
    isDeleted
    dataset {
      id
      name
      description
      datasetAttributes {
        id
        value
        condition {
          id
          field
          fieldType
        }
      }
    }
  }
`;

const PLAYBOOK_TAGS = gql`
  ${CORE_TAG_FIELDS}
  fragment PlaybookTags on PlaybookTag {
    id
    tagId
    isDeleted
    tag {
      ...CoreTagFields
    }
  }
`;

const EXTENDED_PLAYBOOK_FIELDS = gql`
  ${CORE_PLAYBOOK_FIELDS}
  ${PLAYBOOK_DATASET}
  ${PLAYBOOK_TAGS}
  fragment ExtendedPlaybookFields on Playbook {
    ...CorePlaybookFields
    defaultIncidentAccess
    severity {
      id
      name
      ordering
      backgroundColor
      foregroundColor
      isDeleted
    }
    types {
      id
      typeId
      type {
        id
        name
        backgroundColor
        foregroundColor
        isDeleted
      }
    }
    datasets {
      ...PlaybookDataset
    }
    attributes {
      id
      conditionId
      condition {
        id
        field
        fieldType
        listOptions {
          id
          name
          description
        }
      }
      value
    }
    tasks {
      id
      taskId
      task {
        id
        name
        isInTaskLibrary
        documents {
          id
          documentId
          document {
            id
            name
          }
        }
        description
        deadline
        roles {
          id
          roleId
          role {
            id
            name
          }
        }
        relevantIncidentPhase {
          id
          name
          foregroundColor
          backgroundColor
          ordering
        }
      }
    }
    documents {
      id
      documentId
      document {
        id
        name
      }
    }
    tags {
      ...PlaybookTags
    }
    notes {
      id
      note {
        id
        content
      }
    }
    roles {
      id
      roleId
      role {
        id
        name
        isEnabled
      }
    }
    users {
      id
      userId
      user {
        id
        email
        fullName
        isEnabled
      }
    }
  }
`;

export const GET_PLAYBOOKS_CORE = gql`
  ${CORE_PLAYBOOK_FIELDS}
  query GetPlaybooksCore($approved: Boolean, $archived: Boolean, $disabled: Boolean) {
    playbooks(
      where: {
        isApproved: {equals: $approved}
        isArchived: {equals: $archived}
        isDisabled: {equals: $disabled}
      }
      orderBy: {name: asc}
    ) {
      types {
        id
        type {
          id
          name
        }
      }
      ...CorePlaybookFields
    }
  }
`;

export const GET_PLAYBOOKS_LIST = gql`
  ${CORE_PLAYBOOK_FIELDS}
  ${PLAYBOOK_TAGS}
  query GetPlaybooksList($isArchived: Boolean) {
    playbooks(where: {isArchived: {equals: $isArchived}}) {
      ...CorePlaybookFields
      severity {
        id
        name
        ordering
        backgroundColor
        foregroundColor
      }
      tags {
        ...PlaybookTags
      }
    }
  }
`;

export const GET_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  query GetOnePlaybook($id: Int!) {
    playbook(where: {id: $id}) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const GET_PLAYBOOK_WITH_HISTORY = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  query GetOnePlaybookWithHistory($id: Int!) {
    playbook(where: {id: $id}) {
      ...ExtendedPlaybookFields
    }
    playbookHistories(where: {playbookId: {equals: $id}}, orderBy: {timestamp: asc}) {
      id
      event
      raw
      readableRaw
      summary
      timestamp
      user {
        id
        email
        fullName
      }
      playbook {
        id
        name
        description
      }
    }
  }
`;

export const CREATE_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation CreatePlaybook(
    $name: String!
    $description: String
    $severityId: Int!
    $attributeData: JSON
    $datasetIds: [Int!]
    $documentIds: [Int!]
    $noteContent: String
    $tagIds: [Int!]
    $taskIds: [Int!]
    $typeIds: [Int!]
  ) {
    createPlaybook(
      name: $name
      description: $description
      severityId: $severityId
      attributeData: $attributeData
      datasetIds: $datasetIds
      documentIds: $documentIds
      noteContent: $noteContent
      tagIds: $tagIds
      taskIds: $taskIds
      typeIds: $typeIds
    ) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const CREATE_PLAYBOOK_WITH_TASKS = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation ConvertIncidentWithTasksIntoPlaybook(
    $name: String!
    $identifier: String!
    $description: String!
    $severityId: Int!
    $attributes: PlaybookAttributeCreateNestedManyWithoutPlaybookInput
    $datasets: PlaybookDatasetCreateNestedManyWithoutPlaybookInput
    $documents: PlaybookDocumentCreateNestedManyWithoutPlaybookInput
    $noteContent: String!
    $tags: PlaybookTagCreateNestedManyWithoutPlaybookInput
    $tasks: PlaybookTaskCreateNestedManyWithoutPlaybookInput
    $uniqueTasks: [TaskCreateInput!]
    $types: PlaybookTypeCreateNestedManyWithoutPlaybookInput
    $playbookVersion: PlaybookVersion!
  ) {
    createPlaybookWithTasks(
      data: {
        name: $name
        identifier: $identifier
        description: $description
        severity: {connect: {id: $severityId}}
        attributes: $attributes
        datasets: $datasets
        documents: $documents
        notes: {create: {note: {create: {content: $noteContent}}}}
        tags: $tags
        tasks: $tasks
        types: $types
        playbookVersion: $playbookVersion
      }
      tasksToCreate: $uniqueTasks
    ) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const UPDATE_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation UpdatePlaybook(
    $id: Int!
    $name: StringFieldUpdateOperationsInput
    $description: NullableStringFieldUpdateOperationsInput
    $severity: IncidentSeverityUpdateOneRequiredWithoutPlaybooksNestedInput
    $attributes: PlaybookAttributeUpdateManyWithoutPlaybookNestedInput
    $datasets: PlaybookDatasetUpdateManyWithoutPlaybookNestedInput
    $documents: PlaybookDocumentUpdateManyWithoutPlaybookNestedInput
    $notes: PlaybookNoteUpdateManyWithoutPlaybookNestedInput
    $tags: PlaybookTagUpdateManyWithoutPlaybookNestedInput
    $tasks: PlaybookTaskUpdateManyWithoutPlaybookNestedInput
    $types: PlaybookTypeUpdateManyWithoutPlaybookNestedInput
    $playbookVersion: EnumPlaybookVersionFieldUpdateOperationsInput
  ) {
    updateOnePlaybook(
      where: {id: $id}
      data: {
        name: $name
        description: $description
        severity: $severity
        attributes: $attributes
        datasets: $datasets
        documents: $documents
        notes: $notes
        tags: $tags
        tasks: $tasks
        types: $types
        playbookVersion: $playbookVersion
      }
    ) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const DELETE_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation DeletePlaybook($id: Int) {
    deleteOnePlaybook(where: {id: $id}) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const ARCHIVE_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation ArchivePlaybook($id: Int!, $archive: Boolean!) {
    updateOnePlaybook(where: {id: $id}, data: {isArchived: {set: $archive}}) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const SET_IS_DYNAMIC_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation SetIsDynamicPlaybook($id: Int!, $isDynamic: Boolean!) {
    updateOnePlaybook(where: {id: $id}, data: {isDynamic: {set: $isDynamic}}) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const APPROVE_PLAYBOOK = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation ApprovePlaybook($id: Int!, $isApproved: Boolean) {
    updateOnePlaybook(where: {id: $id}, data: {isApproved: {set: $isApproved}}) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const UPDATE_PLAYBOOK_IS_DISABLED = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation UpdatePlaybookIfDisabled($id: Int, $isDisabled: Boolean!) {
    updateOnePlaybook(where: {id: $id}, data: {isDisabled: {set: $isDisabled}}) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const UPDATE_PLAYBOOK_ACCESS = gql`
  ${EXTENDED_PLAYBOOK_FIELDS}
  mutation UpdatePlaybookAccess(
    $id: Int
    $defaultIncidentAccess: Boolean
    $roles: PlaybookRoleUpdateManyWithoutPlaybookNestedInput
    $users: PlaybookUserUpdateManyWithoutPlaybookNestedInput
  ) {
    updateOnePlaybook(
      where: {id: $id}
      data: {defaultIncidentAccess: {set: $defaultIncidentAccess}, roles: $roles, users: $users}
    ) {
      ...ExtendedPlaybookFields
    }
  }
`;

export const ADD_PLAYBOOK_FILES = gql`
  ${CORE_PLAYBOOK_FIELDS}
  mutation AddPlaybookDocuments(
    $id: Int!
    $documentsToCreate: [PlaybookDocumentCreateManyPlaybookInput!]!
  ) {
    updateOnePlaybook(
      where: {id: $id}
      data: {documents: {createMany: {data: $documentsToCreate}}}
    ) {
      ...CorePlaybookFields
      documents {
        id
        documentId
        document {
          id
          name
        }
      }
    }
  }
`;

export const DELETE_PLAYBOOK_FILES = gql`
  ${CORE_PLAYBOOK_FIELDS}
  mutation DeletePlaybookDocuments($id: Int!, $documentsToDelete: [Int!]) {
    updateOnePlaybook(
      where: {id: $id}
      data: {documents: {deleteMany: {documentId: {in: $documentsToDelete}}}}
    ) {
      ...CorePlaybookFields
      documents {
        id
        documentId
        document {
          id
          name
        }
      }
    }
  }
`;
